import { get, set } from "lodash";
import mapKeys from "lodash/mapKeys";
import pickBy from "lodash/pickBy";

import { AssessConfiguration, ScoreFactor } from "fond/types";
import { base58Decode, base58Encode } from "fond/utils/base58";

export type ScoringInputConfig = {
  id:
    | "AddressDensityImportance"
    | "AddressUnderservedProportionImportance"
    | "FiberCoverageProportionImportance"
    | "WeightedMedianIncomeImportance"
    | "AddressBreakdownImportance.Sdu"
    | `EncodedProviderImportance.${string}`
    | "AddressPathDensityImportance";
  // An optional path specifying the location corresponding to this input in the assess configuration
  path?: string | string[];
  title: string;
  description: string;
  factor: ScoreFactor;
};

/**
 * Return the field ID for the given provider.
 * @param provider The name of the provider
 */
export const getProviderFieldId = (provider: string): `EncodedProviderImportance.${string}` => {
  return `EncodedProviderImportance.${base58Encode(provider)}` as const;
};

/**
 * Create a scoring input configuration for avoiding a specific provider.
 * @param provider The name of the provider
 */
export const createProviderScoringInputConfig = (provider: string): ScoringInputConfig => ({
  // Many provider names have funky characters in them (e.g. dots), which react-final-form doesn't like
  // in field IDs. To avoid this, we encode the provider name as a base58 string, avoiding any funky characters.
  id: getProviderFieldId(provider),
  path: ["ProviderFiberCoverageImportance", provider],
  title: `Avoid ${provider}`,
  factor: "revenue" as ScoreFactor,
  description: `Subarea score will be higher in areas where ${provider} has less fiber coverage`,
});

export const baseScoringInputConfigs = [
  {
    id: "AddressDensityImportance",
    title: "Addresses per area",
    factor: "cost",
    description: "Subarea score will increase as the density of addresses per unit area increases",
  },
  {
    id: "AddressUnderservedProportionImportance",
    title: "Proportion underserved",
    factor: "revenue",
    description: "Subarea score will increase as the underserved proportion increases",
  },
  {
    id: "FiberCoverageProportionImportance",
    title: "Avoid fiber coverage",
    factor: "revenue",
    description: "Subarea score will be higher in areas with less fiber coverage",
  },
  {
    id: "WeightedMedianIncomeImportance",
    title: "Median Income",
    factor: "revenue",
    description: "Subarea score will increase as median income increases",
  },
  {
    id: "AddressBreakdownImportance.Sdu",
    title: "SDU Proportion",
    factor: "revenue",
    description: "Subarea score will increase as the SDU proportion increases",
  },
  {
    id: "AddressPathDensityImportance",
    title: "Addresses per linear distance",
    factor: "cost",
    description: "Subarea score will increase as the density of addresses per unit linear distance increases",
  },
] as const satisfies ScoringInputConfig[];

/**
 * The default values to apply to get someone started with scoring inputs.
 * These are the values that will be applied when a user first opens the scoring panel and all the values are null.
 */
const defaultAssessConfigurationFieldValues: Partial<Record<ScoringInputConfig["id"], number>> = {
  AddressDensityImportance: 0.5,
  AddressUnderservedProportionImportance: 0.5,
  WeightedMedianIncomeImportance: 0.5,
};

/**
 * Will get a default assess configuration with all values set to null except those defined in defaultAssessConfigurationFieldValues.
 * Will return an {@link AssessConfiguration} from the configured default fieldname/value map.
 */
export const getDefaultAssessConfiguration = (): AssessConfiguration => {
  const baseConfig = {
    ...baseScoringInputConfigs.reduce((acc, item) => set(acc, item.id, null), {}),
    ProviderFiberCoverageImportance: {},
  };
  Object.entries(defaultAssessConfigurationFieldValues).forEach(([id, defaultValue]) => set(baseConfig, id, defaultValue ?? null));
  return baseConfig as AssessConfiguration;
};

export const DEFAULT_SCORING_INPUT_VALUE = 0.5;

/**
 * Check if the given assess configuration has any values, optionally filtered by factor
 * @param assessConfiguration - the assess configuration to check
 * @param scoringInputConfigs - the scoring input configurations to check against
 * @param factor - the factor to filter by
 */
export const assessConfigurationHasValues = (
  assessConfiguration: AssessConfiguration | undefined,
  scoringInputConfigs: ScoringInputConfig[],
  factor?: ScoreFactor
): boolean => {
  const foundValues = scoringInputConfigs.filter((item) => get(assessConfiguration, item.path ?? item.id) !== null);
  if (!factor) {
    return foundValues.length > 0;
  }
  return getFactorScoringInputConfig(factor, foundValues).length > 0;
};

/**
 * Get the scoring input configuration for a given factor
 * @param factor
 * @param configToFilter - the configuration to filter
 */
export const getFactorScoringInputConfig = (factor: ScoreFactor, configToFilter: ScoringInputConfig[]): ScoringInputConfig[] => {
  return configToFilter.filter((item) => item.factor === factor);
};

export type ScoringFormData =
  | (Exclude<AssessConfiguration, "ProviderFiberCoverageImportance"> & {
      EncodedProviderImportance: { [encodedProvider: string]: number | null | undefined };
    })
  | null;

/**
 * Encode the assess configuration to be suitable for form state.
 *
 * The main change is encoding the ProviderFiberCoverageImportance keys to avoid funky characters in field IDs.
 *
 * @param config The assess configuration to encode
 */
export const encodeAssessConfiguration = (config: AssessConfiguration): ScoringFormData => {
  if (!config) return null;

  const { ProviderFiberCoverageImportance, ...rest } = config;
  return {
    ...rest,
    EncodedProviderImportance: mapKeys(ProviderFiberCoverageImportance, (_value, provider) => base58Encode(provider)),
  } as ScoringFormData;
};

/**
 * Decode the form state back to an assess configuration.
 *
 * @param data The form state to decode
 */
export const decodeFormData = (data: ScoringFormData): AssessConfiguration => {
  if (!data) return null;

  const { EncodedProviderImportance, ...rest } = data;
  return {
    ...rest,
    ProviderFiberCoverageImportance: mapKeys(
      pickBy(EncodedProviderImportance, (value) => value !== null && value !== undefined),
      (_value, encodedProvider) => base58Decode(encodedProvider)
    ),
  };
};
